import { translationKey } from '../i18n';
import { localize } from '../i18n';
import { getToday, getMedications } from '../api';

function isActiveOnDate(med, dateStr) {
    if (med.active === false || (med.pausedUntil && dateStr <= med.pausedUntil)) return false;
    if (med.paused || (med.globalPausedUntil && dateStr <= med.globalPausedUntil)) return false;
    if (med.frequencyType === 'AS_NEEDED') return false;
    if (med.startDate && dateStr < med.startDate) return false;
    if (med.endDate && dateStr > med.endDate) return false;
    if (med.frequencyType === 'SPECIFIC_DAYS' && med.specificDays) {
        const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        const [y, m, d] = dateStr.split('-').map(Number);
        return med.specificDays.includes(dayNames[new Date(y, m - 1, d).getDay()]);
    }
    if (med.frequencyType === 'EVERY_OTHER_DAY' && med.startDate) {
        const [sy, sm, sd] = med.startDate.split('-').map(Number);
        const [dy, dm, dd] = dateStr.split('-').map(Number);
        return Math.round((new Date(dy, dm - 1, dd) - new Date(sy, sm - 1, sd)) / 86400000) % 2 === 0;
    }
    if (med.frequencyType === 'EVERY_N_DAYS' && med.intervalDays && med.startDate) {
        const [sy, sm, sd] = med.startDate.split('-').map(Number);
        const [dy, dm, dd] = dateStr.split('-').map(Number);
        return Math.round((new Date(dy, dm - 1, dd) - new Date(sy, sm - 1, sd)) / 86400000) % med.intervalDays === 0;
    }
    if (med.frequencyType === 'CYCLE' && med.cycleDaysOn && med.cycleDaysOff && med.startDate) {
        const [sy, sm, sd] = med.startDate.split('-').map(Number);
        const [dy, dm, dd] = dateStr.split('-').map(Number);
        const diff = Math.round((new Date(dy, dm - 1, dd) - new Date(sy, sm - 1, sd)) / 86400000);
        return (diff % (med.cycleDaysOn + med.cycleDaysOff)) < med.cycleDaysOn;
    }
    if (med.frequencyType === 'MONTHLY' && med.monthlyDays) {
        return med.monthlyDays.split(',').map(x => parseInt(x)).includes(parseInt(dateStr.split('-')[2]));
    }
    return true;
}

function pad(n) { return String(n).padStart(2, '0'); }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export function scheduledTimesOn(med, dateStr) {
    if (med.frequencyType !== 'HOURLY_INTERVAL') return med.times || [];
    const first = [...(med.times || [])].sort()[0];
    if (!first || !med.startDate || dateStr < med.startDate) return [];
    // UTC arithmetic represents local calendar fields without DST shifting the interval.
    const anchor = Date.parse(`${med.startDate}T${first.slice(0, 5)}:00Z`);
    const day = Date.parse(`${dateStr}T00:00:00Z`);
    const interval = Math.max(1, Math.min(24, med.hourlyInterval || 1)) * 3600000;
    let next = anchor + Math.max(0, Math.ceil((day - anchor) / interval)) * interval;
    const times = [];
    while (next < day + 86400000) {
        times.push(new Date(next).toISOString().slice(11, 16));
        next += interval;
    }
    return times;
}

export function nextScheduledEntries(meds, now = new Date(), globalPausedUntil = null) {
    for (let offset = 0; offset <= 730; offset++) {
        const day = new Date(now); day.setDate(day.getDate() + offset);
        const dateStr = toDateStr(day);
        if (globalPausedUntil && dateStr <= globalPausedUntil) continue;
        const entries = [];
        for (const med of meds || []) {
            if (!isActiveOnDate(med, dateStr)) continue;
            for (const time of scheduledTimesOn(med, dateStr)) {
                const [h, min] = time.split(':').map(Number);
                const date = new Date(day); date.setHours(h, min, 0, 0);
                if (date > now) entries.push({ date, med: med.name, time: time.slice(0, 5), label: offset === 0 ? translationKey('오늘') : offset === 1 ? translationKey('내일') : localize("{v0}일 뒤", { v0: offset }) });
            }
        }
        if (entries.length) return entries.sort((a, b) => a.date - b.date);
    }
    return [];
}

export function buildWidgetPayload({ result, meds }) {
    const medsArr = meds || [];
    const now = new Date();
    const today = toDateStr(now);
    const globalPaused = result?.globalPausedUntil && today <= result.globalPausedUntil;
    const logs = (result?.logs || []).filter(log => log.frequencyType !== 'AS_NEEDED' &&
        (log.status !== 'PENDING' || (!globalPaused &&
        !medsArr.some(med => med.id === log.medicationId && (med.active === false || med.paused ||
            (med.pausedUntil && today <= med.pausedUntil) || (med.globalPausedUntil && today <= med.globalPausedUntil))))));
    const taken = logs.filter(l => l.status === 'TAKEN').length;
    const total = logs.length;
    const effectiveAt = log => log.deferredUntil || `${log.scheduledDate || today}T${log.scheduledTime}`;
    const effectiveLabel = log => {
        const at = effectiveAt(log);
        return `${at.slice(0, 10) > today ? at.slice(5, 10) + ' ' : ''}${at.slice(11, 16)}`;
    };
    const pending = logs.filter(log => log.status === 'PENDING' && !globalPaused &&
        !medsArr.some(med => med.id === log.medicationId && (med.paused || (med.pausedUntil && today <= med.pausedUntil))))
        .sort((a, b) => effectiveAt(a).localeCompare(effectiveAt(b)) || a.id - b.id);
    let nextMed = pending[0]?.medicationName || null;
    let nextTime = pending[0] ? effectiveLabel(pending[0]) : null;
    if (!nextMed) {
        const next = nextScheduledEntries(medsArr, now, result?.globalPausedUntil)[0];
        if (next) { nextMed = next.med; nextTime = `${next.label === translationKey('오늘') ? '' : localize(next.label) + ' '}${next.time}`; }
    }

    let medList;
    if (logs.length > 0) {
        medList = [...logs].sort((a, b) => effectiveAt(a).localeCompare(effectiveAt(b)) || a.id - b.id).map(l => ({
            name: l.medicationName,
            time: l.status === 'PENDING' ? effectiveLabel(l) : l.scheduledTime?.slice(0, 5),
            status: l.status,
        }));
    } else {
        const today = toDateStr(new Date());
        medList = medsArr
            .filter(m => !globalPaused && isActiveOnDate(m, today))
            .map(m => ({
                name: m.name,
                time: (m.times && m.times[0]) ? m.times[0].slice(0, 5) : '',
                status: 'PENDING',
            }));
    }
    // Large widgets have room for three medication rows and one overflow row.
    // Keep the count before truncation so the user can see that more doses exist.
    if (medList.length > 3) {
        medList = [...medList.slice(0, 3), { name: localize("+{v0}개 더보기", { v0: medList.length - 3 }), time: '', status: 'MORE' }];
    }

    const hasMedications = medsArr.length > 0 || logs.length > 0;

    return {
        taken, total,
        streak: result?.streak || 0,
        nextMed, nextTime,
        medications: JSON.stringify(medList),
        hasMedications,
    };
}

export function postWidgetUpdate(payload) {
    if (!window.ReactNativeWebView) return;
    try {
        window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'UPDATE_WIDGET',
            data: payload,
        }));
    } catch {}
}

export async function syncWidget(uuid) {
    if (!window.ReactNativeWebView) return;
    try {
        const [result, meds] = await Promise.all([getToday(uuid), getMedications(uuid)]);
        postWidgetUpdate(buildWidgetPayload({ result, meds }));
    } catch {}
}
